import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Product } from '../types/api';

interface ProductGridProps {
  products: Product[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

const ProductGrid: React.FC<ProductGridProps> = ({ products, loading, error, onRetry }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('home');

  const handleProductClick = (productId: number) => {
    navigate(`/checkout/${productId}`);
  };

  // Fallback image for broken/missing logos
  const fallbackImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='400' viewBox='0 0 300 400'%3E%3Crect width='300' height='400' fill='%23f3f4f6'/%3E%3Cpath d='M150 160c-11 0-20 9-20 20v20h40v-20c0-11-9-20-20-20zm-30-10c0-16.5 13.5-30 30-30s30 13.5 30 30v10h10c11 0 20 9 20 20v60c0 11-9 20-20 20h-80c-11 0-20-9-20-20v-60c0-11 9-20 20-20h10v-10z' fill='%239ca3af'/%3E%3C/svg%3E";

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="bg-gray-300 h-48 md:h-56 lg:h-64 rounded-lg"></div>
              <div className="mt-3 h-4 bg-gray-300 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={onRetry}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:brightness-90"
          >
            {t('retry')}
          </button>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-gray-500">{t('no_products')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Grid - 2 columns on mobile, 3 on tablet, 6 on desktop */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
        {products.map((product) => (
          <div
            key={product.id}
            onClick={() => handleProductClick(product.id)}
            className="group cursor-pointer transform transition-all duration-300 hover:scale-105"
          >
            {/* Product Image */}
            <div className="relative overflow-hidden rounded-lg shadow-md group-hover:shadow-xl">
              <img
                src={product.logoUrl || fallbackImage}
                alt={product.title}
                className="w-full h-48 md:h-56 lg:h-64 object-cover"
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  if (img.src !== fallbackImage) {
                    img.src = fallbackImage;
                  }
                }}
              />
              <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity"></div>
            </div>

            {/* Product Name */}
            <h3 className="mt-3 text-center text-sm md:text-base font-semibold text-gray-800 group-hover:text-primary transition-colors">
              {product.title}
            </h3>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductGrid;
